/**
 * /signin — where the emailed login link lands.
 *
 * Every login mail the app has ever sent points here (`/signin?token=…`) and
 * this route did not exist on either side of the router, so the token was
 * dropped on the floor and the tap ended on the passcode screen. That is the
 * same class of miss as `/verify`: an endpoint built, a link generated, and no
 * screen to receive it.
 *
 * It does one thing and needs no prior state, which matters because the tap
 * usually happens in a mail client's webview on a different device from the one
 * that asked. No session, no `sessionStorage`, nothing but the token in the
 * URL.
 *
 * Redeeming also confirms the address (see `signinComplete`), so arriving here
 * is how an account stops being takeable.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useStore } from '../lib/store'
import { ApiFailure, POST } from '../lib/api'
import { Button, Notice, Screen, Spacer } from '../ui'
import { Wordmark } from '../ui/Wordmark'

export default function SignIn() {
  const navigate = useNavigate()
  const { refresh } = useStore()
  const [params] = useSearchParams()
  const token = params.get('token')

  const [err, setErr] = useState<string | null>(null)
  // Single use, and React invokes effects twice in development. Redeeming a
  // burned token reports failure on what was actually a success.
  const tried = useRef(false)

  const redeem = useCallback(async (raw: string) => {
    try {
      await POST('/api/auth/signin/complete', { token: raw })
      await refresh()
      navigate('/', { replace: true })
    } catch (e) {
      setErr(
        e instanceof ApiFailure
          ? e.message
          : 'That link didn’t work. It may have been used already, or expired.',
      )
    }
  }, [navigate, refresh])

  useEffect(() => {
    if (!token || tried.current) return
    tried.current = true
    void redeem(token)
  }, [token, redeem])

  return (
    <Screen style={{ justifyContent: 'center', padding: '0 26px' }}>
      <main style={{ width: '100%', maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Wordmark width={250} style={{ color: 'var(--fg)' }} />
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '14px 0 0', letterSpacing: '-0.02em' }}>
          {err ? 'That link is no good' : 'Signing you in…'}
        </h1>

        {!token ? (
          <>
            <Spacer h={16} />
            <div style={{ fontSize: 14, color: 'var(--dim)', lineHeight: 1.5 }}>
              This page is for the link in your email. Open that link, or start again below.
            </div>
          </>
        ) : null}

        {err ? (
          <>
            <Spacer h={18} />
            <Notice tone="alert">{err}</Notice>
          </>
        ) : null}

        {err || !token ? (
          <>
            <Spacer h={22} />
            <Button onClick={() => navigate('/join', { replace: true })}>Back to the start</Button>
          </>
        ) : null}
      </main>
    </Screen>
  )
}
